import { useState, useCallback } from 'react'

export const DELETE_ACCOUNT_TYPES = {
  DELETE_ACCOUNT: 'delete-account',
} as const

export const DELETE_ACCOUNT_ERROR_REASONS = {
  CANCELLATION_REQUIRED: 'cancellation-required',
  PAYMENT_REQUIRED: 'payment-required',
  DEFAULT: 'default',
} as const

export type DeleteAccountType = (typeof DELETE_ACCOUNT_TYPES)[keyof typeof DELETE_ACCOUNT_TYPES]
export type DeleteAccountErrorReason = (typeof DELETE_ACCOUNT_ERROR_REASONS)[keyof typeof DELETE_ACCOUNT_ERROR_REASONS]

interface DeleteAccountState {
  isDeleteModalOpen: boolean
  isErrorModalOpen: boolean
  errorType: DeleteAccountType | null
  deleteErrorReason: DeleteAccountErrorReason | null
}

interface DeleteAccountActions {
  openDeleteModal: () => void
  closeDeleteModal: () => void
  openErrorModal: () => void
  closeErrorModal: () => void
  handleDeleteSuccess: () => void
  handleDeleteError: () => void
}

type UseDeleteAccount = DeleteAccountState & DeleteAccountActions

export const useDeleteAccount = (): UseDeleteAccount => {
  const [state, setState] = useState<DeleteAccountState>({
    isDeleteModalOpen: false,
    isErrorModalOpen: false,
    errorType: null,
    deleteErrorReason: null,
  })

  const openDeleteModal = useCallback(() => {
    setState(prev => ({
      ...prev,
      isDeleteModalOpen: true,
      isErrorModalOpen: false,
      errorType: null,
      deleteErrorReason: null,
    }))
  }, [])

  const closeDeleteModal = useCallback(() => {
    setState(prev => ({
      ...prev,
      isDeleteModalOpen: false,
    }))
  }, [])

  const openErrorModal = useCallback((reason: DeleteAccountErrorReason = DELETE_ACCOUNT_ERROR_REASONS.DEFAULT) => {
    setState(prev => ({
      ...prev,
      isDeleteModalOpen: false,
      isErrorModalOpen: true,
      errorType: DELETE_ACCOUNT_TYPES.DELETE_ACCOUNT,
      deleteErrorReason: reason,
    }))
  }, [])

  const closeErrorModal = useCallback(() => {
    setState(prev => ({
      ...prev,
      isErrorModalOpen: false,
      errorType: null,
      deleteErrorReason: null,
    }))
  }, [])

  const handleDeleteSuccess = useCallback(() => {
    setState(prev => ({
      ...prev,
      isDeleteModalOpen: false,
      isErrorModalOpen: false,
      errorType: null,
      deleteErrorReason: null,
    }))
  }, [])

  const handleDeleteError = useCallback(
    (reason: DeleteAccountErrorReason = DELETE_ACCOUNT_ERROR_REASONS.DEFAULT) => {
      openErrorModal(reason)
    },
    [openErrorModal],
  )

  return {
    ...state,
    openDeleteModal,
    closeDeleteModal,
    openErrorModal,
    closeErrorModal,
    handleDeleteSuccess,
    handleDeleteError,
  }
}
